import { useMutation } from "@tanstack/react-query";

import { ApiError } from "@config/apiClient";
import dealService from "@services/deal.service";
import ToastService from "@services/toast.service";
import { ERROR_STRINGS, ERROR_MESSAGES, UPLOAD_ERROR_MESSAGES } from "@config/messages";
import { useCreateMeetingObject } from "@screens/meeting/hooks/useCreateMeetingObject";
import { useBugsnagErrorHandler } from "@hooks/useBugsnagErrorHandler";
import { DisconnectedError } from "@app-types/errors";
import { CreateDealRequest } from "@app-types/requests.type";
import { CREATE_DEAL_MUTATION_KEY } from "@config/query";
import { RelationObjectType } from "@enums/relations.enum";

export function useCreateDeal(contentType: RelationObjectType, onSuccess?: VoidFunction) {
  const { notifyBugsnag } = useBugsnagErrorHandler();
  const { mutateAsync: createMeetingObject } = useCreateMeetingObject();

  return useMutation({
    mutationKey: [CREATE_DEAL_MUTATION_KEY],
    mutationFn: async (newDeal: CreateDealRequest) => {
      const formData = new FormData();

      formData.append("description", newDeal.description || "");
      formData.append("source", newDeal.source);
      formData.append("stage", newDeal.stage);
      formData.append("title", newDeal.title || "");

      newDeal.team && newDeal.team.length > 0 && formData.append("team", newDeal.team.join(","));
      newDeal.deal_source && formData.append("deal_source", JSON.stringify(newDeal.deal_source));
      newDeal.company && formData.append("company", newDeal.company);

      newDeal.external_company &&
        formData.append("external_company", JSON.stringify(newDeal.external_company));

      newDeal.update_company_data &&
        formData.append("update_company_data", JSON.stringify(newDeal.update_company_data));

      if (newDeal.files && newDeal.files.length > 0) {
        newDeal.files.forEach((file) => {
          formData.append("file", file);
        });
      } else if (newDeal.document_file) {
        formData.append("document_file", newDeal.document_file);
      }

      newDeal.theme_ids?.forEach((id) => {
        formData.append("theme_ids", id);
      });

      newDeal.attribute_values?.length &&
        formData.append("attribute_values", JSON.stringify(newDeal.attribute_values));

      newDeal.currency && formData.append("currency", newDeal.currency);

      return await dealService.createDeal(formData);
    },
    onSuccess: (response) => {
      onSuccess?.();
      if (contentType === RelationObjectType.MEETING) {
        void createMeetingObject({
          object_type: RelationObjectType.DEAL,
          object_id: response.id,
        });
      }

      ToastService.showSuccess(`"${response.title}" deal created`);
    },
    onError: (error) => {
      if (error instanceof DisconnectedError) return;

      const axiosError = error as ApiError;
      const apiMessage = ToastService.getErrorMessage(axiosError);
      const messages = Array.isArray(apiMessage) ? apiMessage : [apiMessage];

      // Don't show toast or report to Bugsnag; the dialog will handle this inline
      if (messages.includes(UPLOAD_ERROR_MESSAGES.IC_MEMO_CIM_DUPLICATE)) return;

      notifyBugsnag(error, ERROR_STRINGS.CREATE_DEAL);
      ToastService.showError(axiosError, ERROR_MESSAGES.CREATE_DEAL);
    },
  });
}
